/**
 * Centralized resolution of Cloudflare worker base URLs.
 *
 * Frontend code talks to two workers: the live-session worker (real-time
 * WebSocket audio) and the offline-session worker (conversation generation, objective
 * suggestions, brand research, workspace asset upload). Both URLs come from a
 * `VITE_*` build-time var with a hardcoded fallback so local dev "just works"
 * without forcing every contributor to populate `.env.local`.
 *
 * This module replaces the per-file copies of that env-var read plus fallback
 * that previously lived in `useSessionWebSocket.ts`, `suggest-objectives.ts`,
 * `sse-consumer.ts`, and `WorkspaceLogoUpload.tsx`. Renaming a worker now
 * touches one place instead of five.
 *
 * The fallbacks point at **staging**, not production: this repo is the staging
 * codebase per `CLAUDE.md`, and the offline-session production worker does not
 * exist on the account at time of writing.
 *
 * @module lib/worker-urls
 */
import { getEnv } from './utils/env';
import { IS_PROD } from './utils/env/is-prod';

/**
 * Hardcoded fallback for the live-session worker, used when
 * `VITE_LIVE_SESSION_WORKER_URL` is absent. Preserved exactly from the prior
 * inline constant in `useSessionWebSocket.ts`.
 */
export const FALLBACK_LIVE_SESSION_WORKER_URL =
  'https://holito-live-session.daniel-5f4.workers.dev';

/**
 * Hardcoded fallback for the offline-session worker, used when
 * `VITE_OFFLINE_SESSION_WORKER_URL` is absent. Preserved exactly from the
 * prior inline constants in `suggest-objectives.ts` and `sse-consumer.ts`.
 */
export const FALLBACK_OFFLINE_SESSION_WORKER_URL =
  'https://holito-offline-session-staging.daniel-5f4.workers.dev';

/**
 * MCP server endpoints shown in the connect card. These must match the URL
 * clients actually connect to (the same value the worker advertises as its
 * OAuth resource), or OAuth discovery fails. The branded api.holito.app host is
 * delegated into the workers account (via NS records) and routed to the worker
 * at the `/mcp` path, so these branded hosts are the live endpoints. The host
 * is shared: the public v1 REST API will later claim `api.holito.app/*`.
 */
export const MCP_API_URL_PRODUCTION = 'https://api.holito.app/mcp';
export const MCP_API_URL_STAGING = 'https://api-staging.holito.app/mcp';

/** Public developer documentation page for the holito API. */
export const HOLITO_API_DOCS_URL = 'https://holito.app/docs/api';

/**
 * Resolve the live-session worker base URL. Reads
 * `VITE_LIVE_SESSION_WORKER_URL`, falling back to the hardcoded staging URL.
 * Trailing slashes are trimmed so callers can safely append paths.
 *
 * @returns Base URL with no trailing slash.
 */
export function liveSessionWorkerUrl(): string {
  const url =
    getEnv('VITE_LIVE_SESSION_WORKER_URL', false) || FALLBACK_LIVE_SESSION_WORKER_URL;
  return url.replace(/\/+$/, '');
}

/**
 * Resolve the offline-session worker base URL. Reads
 * `VITE_OFFLINE_SESSION_WORKER_URL`, falling back to the hardcoded staging URL.
 * Trailing slashes are trimmed so callers can safely append paths.
 *
 * @returns Base URL with no trailing slash.
 */
export function offlineSessionWorkerUrl(): string {
  const url =
    getEnv('VITE_OFFLINE_SESSION_WORKER_URL', false) || FALLBACK_OFFLINE_SESSION_WORKER_URL;
  return url.replace(/\/+$/, '');
}

/**
 * Resolve the MCP server endpoint shown to agents. A `VITE_MCP_API_URL`
 * override wins; otherwise the production or staging branded URL is chosen by
 * build environment. Trailing slashes are trimmed.
 *
 * @returns The MCP endpoint URL with no trailing slash.
 */
export function mcpApiUrl(): string {
  const override = getEnv('VITE_MCP_API_URL', false);
  if (override) return override.replace(/\/+$/, '');
  return IS_PROD ? MCP_API_URL_PRODUCTION : MCP_API_URL_STAGING;
}
